/**
 * conceptBadge — 매스컨셉 배지 한 벌(색표 · 배지 · v2 사전 이음매).
 *
 * 같은 배지를 두 곳이 그린다: 문항 편집 행(PaperItemsEditor)과 문제 미리보기(ProblemPreviewV2).
 * 각자 그리면 같은 개념이 자리에 따라 다른 얼굴이 되므로 여기 한 벌만 둔다.
 *
 * 순번(order)·유형(concept_type)은 **개념 자체의 속성**이라 문제 응답에 없다. 그 둘을 주는 것이
 * 이 파일 아래의 fetchConceptDictV2(GET /api/v2/mathcepts, 계약 §8)이고, **두 곳이 같은 것을 쓴다** —
 * 2026-08-26 로 문항 행도 v1(/api/mathcepts/temp)에서 여기로 갈아탔다(그 전에는 문항 행만 v1 이었다).
 * 사전이 한 벌이라 같은 개념이 자리에 따라 다른 순번·색을 갖는 일이 없고, 모듈 캐시(5분)도 함께 쓴다.
 * 이 부품은 받은 값을 그릴 뿐이고, 없으면 이름만 그린다(구형과 같은 조건부 구조).
 */
import { fetchWithTokenRefresh } from '../../utils/apiUtils';
import MathName from '../SyllabusV2/MathName';

/**
 * 개념 유형 색 — 구형 미리보기(ProblemPreview.js:136)의 표를 값 하나 바꾸지 않고 옮겼다.
 * 그 함수가 컴포넌트 안에 갇혀 있어 import 할 수 없다. 같은 표가 KMatrixContent·
 * AITrackerContent 에도 복제돼 있으니, 공용으로 더 올리는 것은 그 셋을 함께 다룰 때 한다.
 */
export const CONCEPT_TYPE_COLOR = {
  DEF: 'rgba(239, 68, 68, 0.5)',   // 빨강 (정의)
  AXM: 'rgba(249, 115, 22, 0.5)',  // 주황 (공리)
  IDN: 'rgba(245, 158, 11, 0.5)',  // 노랑 (정리)
  THM: 'rgba(34, 197, 94, 0.5)',   // 초록 (이론)
  MTH: 'rgba(6, 182, 212, 0.5)',   // 하늘 (방법론)
  PRP: 'rgba(59, 130, 246, 0.5)',  // 파랑 (성질)
  FOR: 'rgba(139, 92, 246, 0.5)',  // 보라 (공식)
  NTN: 'rgba(236, 72, 153, 0.5)',  // 분홍 (표기법)
};
export const conceptTypeColor = (t) => CONCEPT_TYPE_COLOR[t] || 'rgba(251, 146, 60, 0.5)';

/** 순번 칸 배경 — 구형과 같은 값(유형과 달리 종류가 하나라 상수로 둔다) */
const ORDER_BG = 'rgba(156, 163, 175, 0.3)';

/**
 * 개념 배지 — [순번][유형][이름]. 구형 미리보기(ProblemPreview.js:415-453)와 같은 모양이다.
 * compact 는 좁은 자리(문항 편집 행)용으로 한 단 줄인 것이고, 구조·색은 같다.
 *
 * order·type 이 없으면 그 칸을 그리지 않는다 — 구형도 조건부라 사전이 없을 때
 * 이름만 남는 같은 모양이 된다. 그래서 사전이 나중에 붙어도 이 부품은 손댈 것이 없다.
 */
export function ConceptBadge({ name, order = null, type = null, compact = false, onClick = null, asSpan = false }) {
  const box = compact
    ? { borderRadius: '6px', width: '20px', height: '14px', minWidth: '20px' }
    : { borderRadius: '6px', width: '24px', height: '16px', minWidth: '24px' };
  const base = `flex shrink-0 items-center gap-1 whitespace-nowrap rounded-3xl border border-gray-200 text-gray-700 ${
    compact ? 'px-1.5 py-0 !text-[10px]' : 'px-2 py-0.5 text-xs'
  }`;
  const inner = (
    <>
      {order != null && (
        <span
          className="inline-flex items-center justify-center font-semibold text-gray-700"
          style={{ ...box, backgroundColor: ORDER_BG, fontSize: compact ? '8px' : '9px' }}
        >
          {order}
        </span>
      )}
      {type && (
        <span
          className="inline-flex items-center justify-center font-semibold text-gray-800"
          style={{ ...box, backgroundColor: conceptTypeColor(type), fontSize: compact ? '7px' : '8px' }}
        >
          {type}
        </span>
      )}
      {/* 이름 속 $…$ 는 KaTeX 로(오너 2026-10-20 「모든 배지에서 수식 처리」). compact — 배지 글씨가 7~12px 이라 \dfrac 를
          보통 분수로 그려 줄 높이를 지킨다. KaTeX 제 CSS 의 `.katex { font-size: 1.21em }` 은 배지 글자 크기로 되돌린다
          (지면 본문 problemBodyPaper.css 와 같은 이유 — 그대로 두면 수식 든 배지만 2px 높았다). 말풍선(title)은 브라우저가
          수식을 못 그리므로 원문 그대로 둔다. */}
      <span className="[&_.katex]:text-[1em]"><MathName text={name} compact /></span>
    </>
  );

  /**
   * onClick 이 없으면 **종전 그대로 span 이다.** 이 부품을 쓰는 다른 화면(문제 미리보기 등)의
   * 마크업이 버튼으로 바뀌면 그쪽 레이아웃·접근성이 함께 달라지므로, 누를 수 있는 자리만
   * 버튼이 된다. 누를 수 있다는 것은 손 모양과 hover 로만 말한다 — 색이나 밑줄을 더하면
   * 배지가 나르던 [순번][유형][이름] 세 칸의 위계가 흔들린다.
   */
  if (onClick) {
    /**
     * 이 배지는 **누를 수 있는 것 안에** 놓일 수 있다(조립 창의 검색 카드는 배지 줄 전체가
     * 고르기 버튼이고, 담은 문항 행은 교체 모드에서 통째로 '이 자리와 맞바꾸기'다).
     * 전파를 끊지 않으면 설명을 여는 한 번의 클릭이 그 문제를 고르거나 자리를 바꾸는
     * 조작까지 함께 돌린다.
     */
    const hit = (e) => {
      e.preventDefault();
      e.stopPropagation();
      onClick(e);
    };
    /**
     * asSpan — 클릭 가능한 **조상이 button 일 때**. button 안의 button 은 HTML 위반이고
     * React 가 `<button> cannot appear as a descendant of <button>` 을 낸다.
     * 얼굴은 button 경로와 한 글자도 다르지 않고, 마우스 조작도 같다.
     *
     * **초점을 받지 않는다**(role·tabIndex 를 얹지 않는다). 이 모드가 놓이는 자리는
     * 조립 창의 검색 카드(줄 전체가 고르기 버튼)와 담은 문항 행(`aria-hidden` 인 클릭
     * 상자)인데, 거기서 배지 하나마다 탭 정거장을 만들면 **한 화면에 수백 개**가 된다
     * (문항 38 × 개념 7). 게다가 조상이 aria-hidden 이라 보조기기에는 어차피 닿지 않아,
     * role="button" 만 얹으면 「버튼이라 읽히는데 누를 수 없는 것」이 된다.
     * 키보드로 여는 길은 같은 배지의 button 경로가 갖는다(문항 편집기·통계 패널).
     */
    if (asSpan) {
      return (
        <span
          onClick={hit}
          title={`${name} — 눌러서 설명 보기`}
          className={`${base} cursor-pointer transition-colors hover:border-gray-300 hover:bg-gray-50`}
        >
          {inner}
        </span>
      );
    }
    return (
      <button
        type="button"
        onClick={hit}
        title={`${name} — 눌러서 설명 보기`}
        className={`${base} cursor-pointer transition-colors hover:border-gray-300 hover:bg-gray-50`}
      >
        {inner}
      </button>
    );
  }
  return <span title={name} className={base}>{inner}</span>;
}

/**
 * 응답의 개념 목록 → `[{id, name}]`.
 *
 * **이름만 뽑지 않는 이유**는 사전이 붙었을 때 id 로 매칭해 순번·유형을 채워야 하기 때문이다
 * (그때 이 함수를 고칠 필요가 없다). 그래서 problemApi 의 `normalizeConceptNames` 를 쓰지
 * 않는다 — 그쪽은 **문자열 배열**을 돌려주어 id 가 사라지고, 배지가 이름만 남는다.
 *
 * 계약은 `[{concept_id,name}]` 로 통일됐지만(fe-handover) 문자열 배열로 오는 응답도 흡수한다.
 * 그 경우 짝이 되는 `concept_ids` 를 같은 자리에서 빌려 id 를 살린다.
 *
 * 쓰는 곳이 둘이다: 창(ProblemPreviewV2)과 카드(ProblemCardsV2). 두 벌로 두면 한쪽만
 * 고쳐도 빌드는 통과하고 화면도 떠서 조용히 갈린다.
 *
 * @param data 문제 상세(GET /api/v2/problems/{id}) 또는 검색 item — 둘 다 같은 키를 쓴다
 */
export function conceptPairs(data) {
  const v = data?.concept_names;
  if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'object') {
    return v
      .map((c) => ({ id: c?.concept_id ?? c?.id ?? null, name: c?.name ?? c?.text ?? '' }))
      .filter((c) => c.name);
  }
  const ids = Array.isArray(data?.concept_ids) ? data.concept_ids : [];
  if (Array.isArray(v)) {
    return v
      .filter((x) => typeof x === 'string' && x.trim())
      .map((name, i) => ({ id: ids[i] ?? null, name }));
  }
  return [];
}

/**
 * 개념 id → 사전 항목. id 형이 응답마다 갈려 문자열로도 견준다(구형과 같은 매칭).
 */
export function matchConcept(dict, id) {
  if (!Array.isArray(dict) || id == null) return null;
  return dict.find((mc) => mc.id === id || String(mc.id) === String(id)) || null;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * v2 개념 사전 — GET /api/v2/mathcepts (계약 §8)
 *
 * 순번·유형은 **개념 자체의 속성**이라 문제 응답에 없다. 그 둘을 주는 것이 이 사전이다.
 * 요청해 둔 것을(FE-20260826-v2-mathcept-api.md §2-1) 백엔드가 신설해 2026-08-26 배포했고,
 * 이 함수가 그것을 물어 붙인 자리다. 권한은 user_required — 배지 사전에는 PII 가 없어
 * 교사 토큰으로도 열린다(계약 §8).
 *
 * 항목: concept_id · concept_name · math_type · concept_type · order · is_outdated · replaced_to · tags.
 * order 는 교육과정의 **전역 정렬 순번(유니크)** 라 화면에 그대로 찍어도 된다(계약 §8).
 *
 * v1(/api/mathcepts/temp)을 여기에 끌어오지 마라 — 이 자리는 v2 전용 화면을 위한 것이고,
 * v1 을 넣는 순간 그 화면이 v2 전용이 아니게 된다(오너 방침 2026-08-26).
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * 모듈 캐시 — 개념이 717개고 화면 여러 곳이 같은 사전을 부른다. 수명은 v1 사전
 * (utils/mathConceptsUtils.js)과 같은 **5분** 이다 — 같은 성격의 값에 다른 수명을 발명하지 않는다.
 * (그 파일을 import 하지 않는 이유는 위 주석의 마지막 두 줄과 같다 — v1 을 끌어오지 않는다.)
 */
const DICT_TTL_MS = 5 * 60 * 1000;
let dictCache = null;    // 마지막으로 받아 둔 사전 — **성공했을 때만** 채운다
let dictCachedAt = 0;    // 그 시각(Date.now)
let dictInflight = null; // 진행 중 요청 — 겹치면 같은 Promise 를 나눠 써 왕복이 두 번 나가지 않게 한다

/**
 * 배열 자리 흡수 — 계약 §8 은 data 에 total·concept_types·math_types 를 함께 싣는다고만 하고
 * **배열 키 이름을 못박지 않았다.** 그래서 data 가 배열이면 그대로, 아니면 items → list 순으로 본다
 * (PaperComposeModal 의 검색 응답 흡수와 같은 순서 — 이 저장소 관례).
 */
function toDictItems(d) {
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.items)) return d.items;
  if (Array.isArray(d?.list)) return d.list;
  return [];
}

/**
 * v2 개념 사전 — 성공하면 배열, 실패하면 **null**(던지지 않는다).
 * null 이면 ConceptBadge 가 이름만 그린다 — 사전이 붙기 전과 같은 모습이라 화면이 나빠지지 않는다.
 *
 * 돌려주는 항목에 id·name 을 얹는다 — matchConcept 는 `mc.id` 로 찾고, 부르는 쪽은
 * concept_name·order·concept_type 을 그대로 읽는다(스프레드라 원본 키도 전부 살아 있다).
 */
export async function fetchConceptDictV2(getAccessToken) {
  if (dictCache && Date.now() - dictCachedAt < DICT_TTL_MS) return dictCache;
  if (dictInflight) return dictInflight;

  dictInflight = (async () => {
    try {
      const res = await fetchWithTokenRefresh('/api/v2/mathcepts', {}, getAccessToken);
      // 401 이면 공용 헬퍼가 /signin 으로 보내며 null 을 준다 — 캐시하지 않고 그저 없음으로 둔다
      if (!res || res.status !== 'success') return null;
      const list = toDictItems(res.data).map((c) => ({ ...c, id: c.concept_id, name: c.concept_name }));
      dictCache = list;
      dictCachedAt = Date.now();
      return list;
    } catch (e) {
      // 배지는 보조다 — 못 받아도 화면은 이름만으로 선다. **실패는 캐시하지 않는다** — 다음에 다시 묻는다
      console.error('v2 개념 사전 로드 실패:', e);
      return null;
    } finally {
      dictInflight = null;
    }
  })();
  return dictInflight;
}

/**
 * 개념 **상세**(설명 포함) — `GET /api/v2/mathcepts?concept_ids=…&with_description=1`
 *
 * 위 사전(fetchConceptDictV2)과 같은 엔드포인트지만 두 가지가 다르다:
 *  ① `with_description=1` 로 **`descriptions` 를 함께 받는다**(계약 §8). 사전에는 그 필드가 없다 —
 *     717개 전부에 설명을 실으면 캐시가 무거워지기 때문이고, 그래서 설명은 누를 때만 받는다.
 *  ② 캐시하지 않는다. 사용자가 배지를 누른 그때의 한 왕복이다.
 *
 * **v1(`/api/mathcepts/temp/by-concept-ids`)을 쓰지 않는다.** 공용 상세 모달
 * (`common/MathceptDetailModal`)은 conceptIds 를 주면 스스로 그 v1 을 치는데, 이 화면은 v2 전용이라
 * (오너 방침 2026-08-26) 그 길을 열지 않는다. 대신 여기서 v2 로 받아 모달에 `data` 로 넘긴다 —
 * 모달은 data 가 있으면 조회를 건너뛴다.
 *
 * 실패하면 null(던지지 않는다). 부르는 쪽이 「불러오지 못했습니다」로 말한다.
 */
export async function fetchConceptDetailV2(conceptIds, getAccessToken) {
  const ids = (Array.isArray(conceptIds) ? conceptIds : [conceptIds])
    .map((v) => String(v ?? '').trim())
    .filter((v) => v !== '');
  if (ids.length === 0) return [];
  try {
    const params = new URLSearchParams({ concept_ids: ids.join(','), with_description: '1' });
    const res = await fetchWithTokenRefresh(`/api/v2/mathcepts?${params}`, {}, getAccessToken);
    if (!res || res.status !== 'success') return null;
    // 모달이 읽는 것은 응답 원본 키(concept_id·concept_name·descriptions·tags…)라 그대로 넘긴다
    return toDictItems(res.data);
  } catch (e) {
    console.error('v2 개념 상세 로드 실패:', e);
    return null;
  }
}
